// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import sampleConnectionToken from '../responses/connection_token';
import samplePaymentIntent from '../responses/payment_intent';
import samplePaymentIntentCapture from '../responses/payment_intent_capture';
import {BuildInstructions, TerminalCards} from '../../../components/left-col';

import type {StepsConfig} from '../../../data/GuideTypes';

const buildInstructions = [
  {
    label: 'Gradle',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.load-stripe.buildInstructions.gradle.body',
        defaultMessage: 'Add the dependencies to your build.gradle file:',
      }),
    },
    command: `implementation "com.stripe:stripeterminal:2.5.2"`,
  },
  {
    label: 'GitHub',
    body: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.load-stripe.buildInstructions.github.body',
        defaultMessage: 'The Stripe Android SDK is open-sourced.',
      }),
    },
    link: {
      href: 'https://github.com/stripe/stripe-android',
      label: {
        message: defineMessage({
          id:
            'docsApp.ib.terminal.android.steps.load-stripe.buildInstructions.github.link',
          defaultMessage: 'View on GitHub',
        }),
      },
    },
  },
];

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-server',
  },
  {
    file: 'server',
    id: 'create-location',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.create-mandatory-location.heading',
        defaultMessage: 'Organize  your readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.create-mandatory-location.body',
        defaultMessage:
          '<a>Create locations</a> to organize your readers. Locations group readers and allows them to automatically download the reader configuration needed for their region of use.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://stripe.com/docs/terminal/fleet/locations"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    file: 'server',
    id: 'connection-token-endpoint',
    section: 'setup-server',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.connection-token-endpoint.heading',
        defaultMessage: 'Create a ConnectionToken endpoint',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.connection-token-endpoint.body',
        defaultMessage:
          'To connect to a reader, your backend needs to give the SDK permission to use the reader with your Stripe account by providing it with the secret from a <connectionTokenA>ConnectionToken</connectionTokenA>. Your backend should only create connection tokens for clients that it trusts. If you’re using Stripe Connect, you should also <scopeA>scope the connection token</scopeA> to the relevant connected accounts.',
      }),
      values: {
        connectionTokenA: (str) => (
          <a href="/docs/api/terminal/connection_tokens" target="_blank">
            {str}
          </a>
        ),
        scopeA: (str) => (
          <a href="/docs/terminal/features/connect" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: sampleConnectionToken,
  },
  {
    id: 'load-stripe',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.load-stripe.heading',
        defaultMessage: 'Install the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.load-stripe.body',
        defaultMessage:
          'To install the SDK, add stripeterminal to the dependencies block of your build.gradle file.',
      }),
    },
    Component() {
      return <BuildInstructions instructions={buildInstructions} />;
    },
  },
  {
    id: 'check-fine-location',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.check-fine-location.heading',
        defaultMessage: 'Verify ACCESS_FINE_LOCATION permission',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.check-fine-location.body',
        defaultMessage:
          'Add a check to make sure that the ACCESS_FINE_LOCATION permission is enabled in your app.',
      }),
    },
  },
  {
    id: 'check-location-permission',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.check-location-permission.heading',
        defaultMessage: 'Verify user location permission',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.check-location-permission.body',
        defaultMessage:
          'Override the <code>onRequestPermissionsResult</code> method in your app and check the permission result to verify that the app user grants location permission.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'fetch-connection-token',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.fetch-connection-token.heading',
        defaultMessage: 'Fetch ConnectionToken',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.fetch-connection-token.body',
        defaultMessage:
          'Implement the ConnectionTokenProvider interface in your app, which defines a single function that requests a connection token from your backend.',
      }),
    },
  },
  {
    id: 'lifecycle',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.lifecycle.heading',
        defaultMessage: 'Configure TerminalLifeCycleObserver',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.lifecycle.body',
        defaultMessage:
          'To prevent memory leaks and ensure proper cleanup of long-running Terminal SDK processes, your application must have the Application subclass where <code>TerminalLifeCycleObserver</code> is configured. This subclass should register activity lifecycle callbacks and implement the <code>onTrimMemory</code> method to notify the SDK to prune its memory usage.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'sdk',
    section: 'setup-sdk',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.sdk.heading',
        defaultMessage: 'Initialize the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.sdk.body',
        defaultMessage:
          'To get started, provide the current application context, the ConnectionTokenProvider, and a TerminalListener object.',
      }),
    },
  },
  {
    id: 'discover-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.discover-reader.m2.heading',
        defaultMessage: 'Discover readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.discover-reader.m2.body',
        defaultMessage:
          'The Stripe Terminal SDK comes with a built-in simulated card reader, so you can develop and test your app without connecting to physical hardware. To use the simulated reader, call <code>discoverReaders</code> to search for readers, with the simulated option set to true.',
      }),
    },
    filter: (options) => ['m2', 'wp3'].includes(options.reader),
  },
  {
    id: 'discover-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.discover-reader.wpe.heading',
        defaultMessage: 'Discover readers',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.discover-reader.wpe.body',
        defaultMessage:
          'The Stripe Terminal SDK comes with a built-in simulated card reader, so you can develop and test your app without connecting to physical hardware. To use the simulated reader, call <code>discoverReaders</code> to search for readers, with the simulated option set to true. You can discover intended readers more easily by <a>filtering by location</a>.',
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/terminal/fleet/locations#internet-reader-discovery"
            target="_blank"
          >
            {str}
          </a>
        ),
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'connect-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.connect-reader.heading',
        defaultMessage: 'Connect to the simulated reader',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.connect-reader.body',
        defaultMessage:
          'When <code>discoverReaders</code> returns a result, call <code>connectInternetReader</code> to connect to the simulated reader.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'connect-reader',
    section: 'reader',
    heading: {
      message: defineMessage({
        id:
          'docsApp.ib.terminal.android.steps.connect-bluetooth-reader.heading',
        defaultMessage: 'Connect to the simulated reader',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.connect-bluetooth-reader.body',
        defaultMessage:
          'When <code>discoverReaders</code> returns a result, call <code>connectBluetoothReader</code> to connect to the simulated reader.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
    filter: (options) => ['wp3', 'm2'].includes(options.reader),
  },
  {
    id: 'create-intent-mobile',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.create-intent-mobile.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.create-intent-mobile.body',
        defaultMessage: `Create a <a>PaymentIntent</a> object using the SDK. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer is only charged once.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
    filter: (options) => ['m2', 'wp3'].includes(options.reader),
  },
  {
    id: 'create-intent',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.create-intent.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.create-intent.body',
        defaultMessage: `Add an endpoint on your server that creates a PaymentIntent. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring they’re only charged once. Return the PaymentIntent's client secret in the response. If you’re using Stripe Connect, you can also specify <a>connected account information</a> based on your platform’s charge logic.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/terminal/features/connect" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'fetch-intent',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.fetch-intent.heading',
        defaultMessage: 'Fetch the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.fetch-intent.body',
        defaultMessage:
          'Make a request to your server for a PaymentIntent to initiate the payment process.',
      }),
    },
    filter: (options) => ['wpe'].includes(options.reader),
  },
  {
    id: 'collect-payment-method',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.collect-payment-method.heading',
        defaultMessage: 'Collect payment method details',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.collect-payment-method.body',
        defaultMessage: `Call <code>collectPaymentMethod</code> with the PaymentIntent’s client secret to collect a payment method. When connected to the simulated reader calling this method immediately updates the PaymentIntent object with a <a>simulated test card</a>. When connected to a physical reader the connected reader waits for a card to be presented.`,
      }),
      values: {
        a: (str) => (
          <a
            href="/docs/terminal/references/testing#simulated-test-cards"
            target="_blank"
          >
            {str}
          </a>
        ),
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'process-payment',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.process-payment.heading',
        defaultMessage: 'Process the payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.process-payment.body',
        defaultMessage:
          'After successfully collecting payment method data, call <code>processPayment</code> with the updated PaymentIntent to process the payment. A successful call results in a PaymentIntent with a status of <code>requires_capture</code>.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'capture-intent-endpoint',
    file: 'server',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.capture-intent-endpoint.heading',
        defaultMessage: 'Create an endpoint to capture the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.capture-intent-endpoint.body',
        defaultMessage:
          'Create an endpoint on your backend that accepts a PaymentIntent ID and sends a request to the Stripe API to capture it.',
      }),
    },
    response: samplePaymentIntentCapture,
  },
  {
    id: 'capture-intent',
    section: 'collect-payment',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.capture-intent.heading',
        defaultMessage: 'Capture the PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.capture-intent.body',
        defaultMessage:
          'Notify your backend to capture the PaymentIntent. In your request send the Payment Intent ID.',
      }),
    },
  },
  {
    id: 'run-server',
    file: 'server',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.run-server.heading',
        defaultMessage: 'Run the application',
      }),
    },
  },
  {
    id: 'mobile-test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.mobile-test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id: 'docsApp.ib.terminal.android.steps.mobile-test-cards.body',
        defaultMessage:
          'Use amounts ending in the following special values to test your integration.',
      }),
    },
    Component: TerminalCards,
  },
]: StepsConfig);
